"use strict";
/** *******************************************************************************************************************
  Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
  
  Licensed under the Apache License, Version 2.0 (the "License").
  You may not use this file except in compliance with the License.
  You may obtain a copy of the License at
  
      http://www.apache.org/licenses/LICENSE-2.0
  
  Unless required by applicable law or agreed to in writing, software
  distributed under the License is distributed on an "AS IS" BASIS,
  WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
  See the License for the specific language governing permissions and
  limitations under the License.                                                                              *
 ******************************************************************************************************************** */
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const react_1 = __importDefault(require("react"));
const clsx_1 = __importDefault(require("clsx"));
const uuid_1 = require("uuid");
const core_1 = require("@material-ui/core");
const LoadingIndicator_1 = __importDefault(require("../LoadingIndicator"));
const StatusIndicator_1 = __importDefault(require("../StatusIndicator"));
const Button_1 = require("../Button");
const useStyles = core_1.makeStyles((theme) => ({
    iconMenuItem: {
        marginRight: theme.spacing(0.4),
    },
    groupMenuItem: {
        paddingLeft: theme.spacing(3),
    },
    subheaderMenuItem: {
        display: 'flex',
        alignItems: 'center',
        pointerEvents: 'none',
    },
    recoveryLink: {
        pointerEvents: 'auto',
    },
    errorText: {
        marginRight: theme.spacing(0.4),
    },
    placeholder: {
        display: 'none',
    },
}));
const renderOptions = (options, classes, disabled = false, groupItem = false) => {
    return options.map((option) => {
        if (option.options) {
            return [
                react_1.default.createElement(core_1.ListSubheader, { key: option.label }, option.label),
                ...renderOptions(option.options, classes, option.disabled, true),
            ];
        }
        else {
            return (react_1.default.createElement(core_1.MenuItem, { key: option.value || option.label, value: option.value, disabled: option.disabled || disabled, className: clsx_1.default({ [classes.groupMenuItem]: groupItem }) },
                option.iconName && (react_1.default.createElement("span", { className: classes.iconMenuItem },
                    react_1.default.createElement(Button_1.ButtonIcon, { type: option.iconName }))),
                option.label));
        }
    });
};
const muiSelectProps = ({ ariaDescribedby, ariaLabelledby, ariaRequired, controlId, disabled, invalid, label, onBlur, onChange, onFocus, selectedOption = { value: '' }, }) => {
    return Object.assign(Object.assign(Object.assign(Object.assign({ disabled, error: invalid, id: controlId, onChange, onClose: onBlur, onOpen: onFocus, value: selectedOption.value }, (ariaLabelledby && { 'aria-labelledby': ariaLabelledby })), (ariaDescribedby && { 'aria-describedby': ariaDescribedby })), (label && { 'aria-label': label })), (ariaRequired && { 'aria-required': ariaRequired }));
};
/**
 * Select components are used for collecting user provided information from a list of options.
 */
const Select = ({ ariaDescribedby, ariaLabelledby, ariaRequired, controlId = uuid_1.v4(), disabled = false, empty, errorText, invalid = false, label, loadingText, onBlur, onChange, onFocus, onRecoveryClick, options = [], placeholder, recoveryText, selectedOption, statusType = 'finished', }) => {
    const classes = useStyles();
    const onRecoveryClickHandler = (event) => {
        event.preventDefault();
        onRecoveryClick === null || onRecoveryClick === void 0 ? void 0 : onRecoveryClick(event);
        event.stopPropagation();
    };
    return (react_1.default.createElement(core_1.Select, Object.assign({ variant: "outlined", displayEmpty: true, fullWidth: true }, muiSelectProps({
        ariaDescribedby,
        ariaLabelledby,
        ariaRequired,
        controlId,
        disabled,
        invalid,
        label,
        onBlur,
        onChange,
        onFocus,
        selectedOption,
    }), { MenuProps: {
            anchorOrigin: {
                vertical: 'bottom',
                horizontal: 'left',
            },
            transitionDuration: 0,
            getContentAnchorEl: null,
        }, "data-testid": "select" }),
        placeholder && (react_1.default.createElement(core_1.MenuItem, { key: "__placeholder__", value: "", disabled: true, className: classes.placeholder }, placeholder)),
        empty && !options.length && (react_1.default.createElement(core_1.ListSubheader, { key: "__status__", className: classes.subheaderMenuItem }, empty)),
        statusType !== 'finished' && (react_1.default.createElement(core_1.ListSubheader, { className: classes.subheaderMenuItem, key: "__status__" },
            statusType === 'loading' && react_1.default.createElement(LoadingIndicator_1.default, { label: loadingText }),
            statusType === 'error' && (react_1.default.createElement(react_1.default.Fragment, null,
                react_1.default.createElement("span", { className: classes.errorText },
                    react_1.default.createElement(StatusIndicator_1.default, { statusType: "negative" }, errorText)),
                recoveryText && (react_1.default.createElement(core_1.Link, { href: "#", onClick: onRecoveryClickHandler, className: classes.recoveryLink }, recoveryText)))))),
        statusType === 'finished' && renderOptions(options, classes)));
};
exports.default = Select;
//# sourceMappingURL=index.js.map